import type { Map as MapboxMap, MapMouseEvent } from 'mapbox-gl';
import { useCallback, useEffect, useRef } from 'react';
import { useMapbox } from '@/contexts/MapboxContext';
import { useCdcStore } from '../stores/useCdcStore';

const RECTANGLE_SOURCE_ID = 'area-traffic-rectangle';
const RECTANGLE_FILL_LAYER_ID = 'area-traffic-rectangle-fill';
const RECTANGLE_OUTLINE_LAYER_ID = 'area-traffic-rectangle-outline';

/**
 * Creates a GeoJSON polygon from two corner points
 */
function createRectangleGeoJSON(
  corner1: [number, number],
  corner2: [number, number],
): GeoJSON.Feature<GeoJSON.Polygon> {
  const [lon1, lat1] = corner1;
  const [lon2, lat2] = corner2;

  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [
        [
          [lon1, lat1],
          [lon2, lat1],
          [lon2, lat2],
          [lon1, lat2],
          [lon1, lat1], // Close the polygon
        ],
      ],
    },
  };
}

/**
 * Adds or updates the rectangle layer on the map
 */
function updateRectangleLayer(map: MapboxMap | null, geojson: GeoJSON.Feature<GeoJSON.Polygon>) {
  if (!map) return;

  // Check if source exists
  if (map.getSource(RECTANGLE_SOURCE_ID)) {
    (map.getSource(RECTANGLE_SOURCE_ID) as mapboxgl.GeoJSONSource).setData(geojson);
  } else {
    // Add source
    map.addSource(RECTANGLE_SOURCE_ID, {
      type: 'geojson',
      data: geojson,
    });

    // Add fill layer
    map.addLayer({
      id: RECTANGLE_FILL_LAYER_ID,
      type: 'fill',
      source: RECTANGLE_SOURCE_ID,
      paint: {
        'fill-color': '#3b82f6',
        'fill-opacity': 0.2,
      },
    });

    // Add outline layer
    map.addLayer({
      id: RECTANGLE_OUTLINE_LAYER_ID,
      type: 'line',
      source: RECTANGLE_SOURCE_ID,
      paint: {
        'line-color': '#3b82f6',
        'line-width': 2,
        'line-dasharray': [2, 2],
      },
    });
  }
}

/**
 * Removes the rectangle layer from the map
 */
function removeRectangleLayer(map: MapboxMap | null) {
  if (!map) return;

  if (map.getLayer(RECTANGLE_FILL_LAYER_ID)) {
    map.removeLayer(RECTANGLE_FILL_LAYER_ID);
  }
  if (map.getLayer(RECTANGLE_OUTLINE_LAYER_ID)) {
    map.removeLayer(RECTANGLE_OUTLINE_LAYER_ID);
  }
  if (map.getSource(RECTANGLE_SOURCE_ID)) {
    map.removeSource(RECTANGLE_SOURCE_ID);
  }
}

/**
 * Hook to handle rectangle drawing on the map for Area Traffic feature
 * User clicks first corner, moves mouse to preview, clicks second corner to complete
 */
export function useRectangleDraw() {
  const { map, isMapReady } = useMapbox();
  const isActive = useCdcStore((state) => state.isRectangleDrawActive);
  const stopRectangleDraw = useCdcStore((state) => state.stopRectangleDraw);
  const setAreaTrafficBbox = useCdcStore((state) => state.setAreaTrafficBbox);

  const firstCorner = useRef<[number, number] | null>(null);

  const cleanup = useCallback(() => {
    if (map.current) {
      map.current.getCanvas().style.cursor = '';
      removeRectangleLayer(map.current);
    }
    firstCorner.current = null;
  }, [map]);

  useEffect(() => {
    if (!map.current || !isMapReady || !isActive) {
      if (map.current && !isActive) {
        // Only remove preview layer when deactivating, not the final selection
        if (firstCorner.current) {
          removeRectangleLayer(map.current);
        }
        map.current.getCanvas().style.cursor = '';
      }
      firstCorner.current = null;
      return;
    }

    const handleClick = (e: MapMouseEvent) => {
      if (!firstCorner.current) {
        // First click - set first corner
        firstCorner.current = [e.lngLat.lng, e.lngLat.lat];
      } else {
        // Second click - complete the rectangle
        const secondCorner: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        const [lon1, lat1] = firstCorner.current;
        const [lon2, lat2] = secondCorner;

        // Calculate bbox
        const minLon = Math.min(lon1, lon2);
        const maxLon = Math.max(lon1, lon2);
        const minLat = Math.min(lat1, lat2);
        const maxLat = Math.max(lat1, lat2);

        // Set the bbox in store with a custom area name
        setAreaTrafficBbox({ minLat, maxLat, minLon, maxLon }, 'Custom Area');

        // Keep the rectangle visible but stop drawing mode
        const geojson = createRectangleGeoJSON(firstCorner.current, secondCorner);
        updateRectangleLayer(map.current, geojson);

        // Update layer style to solid line for final selection
        if (map.current?.getLayer(RECTANGLE_OUTLINE_LAYER_ID)) {
          map.current.setPaintProperty(RECTANGLE_OUTLINE_LAYER_ID, 'line-dasharray', null);
          map.current.setPaintProperty(RECTANGLE_OUTLINE_LAYER_ID, 'line-width', 3);
        }

        // Stop drawing mode
        firstCorner.current = null;
        stopRectangleDraw();
      }
    };

    const handleMouseMove = (e: MapMouseEvent) => {
      if (firstCorner.current) {
        // Preview the rectangle while moving
        const currentPos: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        const geojson = createRectangleGeoJSON(firstCorner.current, currentPos);
        updateRectangleLayer(map.current, geojson);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cleanup();
        stopRectangleDraw();
      }
    };

    map.current.on('click', handleClick);
    map.current.on('mousemove', handleMouseMove);
    document.addEventListener('keydown', handleKeyDown);
    map.current.getCanvas().style.cursor = 'crosshair';

    return () => {
      if (map.current) {
        map.current.off('click', handleClick);
        map.current.off('mousemove', handleMouseMove);
        map.current.getCanvas().style.cursor = '';
      }
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [map, isMapReady, isActive, setAreaTrafficBbox, stopRectangleDraw, cleanup]);

  // Clean up rectangle when bbox is cleared
  const bbox = useCdcStore((state) => state.areaTrafficBbox);
  useEffect(() => {
    if (!bbox && map.current) {
      removeRectangleLayer(map.current);
    }
  }, [bbox, map]);

  return { cleanup };
}
